import { useState } from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { Grid2 as Grid, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress, Tabs, Tab } from "@mui/material";

import { Card, PageHeader, Checkbox } from "@cofano/cck";

import Helmet from "components/helmet/Helmet";
import { request } from "components/utils/Request2";
import useRequest from "components/hooks/useRequest";

import { Language, hasPluginPermission } from "imports/Utils";
import { StatusType } from "__generated__/graphql";
import { useQuery } from "@apollo/client";
import { GetAllStatusesDocument } from "__generated__/view/statuses/graphql/queries.generated";

const NotificationTypes = {
	EMAIL: 1,
	JSON: 2,
	INBOX: 3,
};

const NotificationSettings = () => {

	const [ content ] = useRequest({ defaultResponse: [], url: "notifications/contents" });
	const [ notifications, { trigger } ] = useRequest({ defaultResponse: [], url: "notifications" });
	const [ notificationStatuses, { trigger: triggerNotificationStatuses } ] = useRequest({ defaultResponse: [], url: "notifications/status" });

	const { data: statusesQueryData } = useQuery(GetAllStatusesDocument, {
		variables: {
			search: {
				includeActionStatus: true,
			},
		},
	});

	const [ loading, setLoading ] = useState(false);

	const [ selectedStatusTab, selectStatusTab ] = useState(StatusType.Cargo);

	const updateNotification = (event, id, type, notification) => {

		setLoading(true);

		if (event.target.checked) {
			request({
				url: "notifications",
				method: "POST",
				body: {
					content: id,
					type: type,
				},
			}).then(() => {
				trigger();
				setTimeout(() => setLoading(false), 300);
			});
		} else {
			request({
				url: `notifications/${notification.id}`,
				method: "DELETE",
			}).then(() => {
				trigger();
				setTimeout(() => setLoading(false), 300);
			});
		}
	};

	const updateStatusNotification = (event, id, type, notification) => {

		setLoading(true);

		if (event.target.checked) {
			request({
				url: "notifications/status",
				method: "POST",
				body: {
					status: {
						id: id,
					},
					type: type,
				},
			}).then(() => {
				triggerNotificationStatuses();
				setTimeout(() => setLoading(false), 300);
			});
		} else {
			request({
				url: `notifications/status/${notification.id}`,
				method: "DELETE",
			}).then(() => {
				triggerNotificationStatuses();
				setTimeout(() => setLoading(false), 300);
			});
		}

	};

	const getNotification = (id, type) => {
		return notifications?.find(n => n.content === id && n.type === type);
	};

	const getStatusNotification = (id, type) => {
		return notificationStatuses?.find(n => n.status.id === id && n.type === type);
	};

	return (
		<div className="notification-settings">
			<PageHeader>
				<PageHeader.Title>
					{Language("NOTIFICATIONS_UPDATES")}
					<Helmet title={intl.formatMessage({ id: "NOTIFICATIONS_UPDATES" })} />
				</PageHeader.Title>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={{ xs: 12, lg: 6 }}>
					<div style={{ marginBottom: 16 }}>
						<Card title={Language("GENERAL")}>
							<List
								items={[ ...content ].filter(item => item.category === "GENERAL").map(item => ({
									id: item.id,
									name: intl.formatMessage({ id: `NOTIFICATION_CONTENT_${item.id}` }),
									json: {
										disabled: item.id === 8 || item.id === 33,
										notification: getNotification(item.id, NotificationTypes.JSON),
									},
									email: {
										disabled: item.id === 8,
										notification: getNotification(item.id, NotificationTypes.EMAIL),
									},
									inbox: {
										disabled: item.id === 8,
										notification: getNotification(item.id, NotificationTypes.INBOX),
										alwaysEnabled: item.id === 8,
									},
								}))}
								loading={loading}
								updateNotification={updateNotification} />
						</Card>
					</div>

					<Card title={Language("SHIP")}>
						<List
							items={[ ...content ].filter(item => item.category === "SHIP").map(item => ({
								id: item.id,
								name: intl.formatMessage({ id: `NOTIFICATION_CONTENT_${item.id}` }),
								json: {
									notification: getNotification(item.id, NotificationTypes.JSON),
								},
								email: {
									notification: getNotification(item.id, NotificationTypes.EMAIL),
								},
								inbox: {
									notification: getNotification(item.id, NotificationTypes.INBOX),
								},
							}))}
							loading={loading}
							updateNotification={updateNotification} />
					</Card>
				</Grid>

				<Grid size={{ xs: 12, lg: 6 }}>
					<div style={{ marginBottom: 16 }}>
						<Card title={Language("NOTIFICATIONS_STATUSES")}>
							<Tabs
								variant='scrollable'
								value={selectedStatusTab}
								onChange={(_, newValue) => selectStatusTab(newValue)}
								style={{ marginBottom: 16 }}>
								<Tab label={Language("STATUS_CARGO")} value={StatusType.Cargo} />
								<Tab label={Language("STATUS_VOYAGE")} value={StatusType.Voyage} />
								<Tab label={Language("STATUS_CALL")} value={StatusType.Call} />
							</Tabs>

							<List
								items={statusesQueryData?.allStatuses
									.filter(s => s.type === selectedStatusTab)
									.map(item => ({
										id: item.id,
										name: item.description,
										json: {
											notification: getStatusNotification(item.id, NotificationTypes.JSON),
										},
										email: {
											notification: getStatusNotification(item.id, NotificationTypes.EMAIL),
										},
										inbox: {
											notification: getStatusNotification(item.id, NotificationTypes.INBOX),
										},
									})) ?? []}
								loading={loading}
								updateNotification={updateStatusNotification} />
						</Card>
					</div>
				</Grid>
			</Grid>
		</div>
	);
};

const List = ({ items, loading, updateNotification }) => {

	const session = useStoreState(state => state.session);

	return (
		<div className="list">
			<Table size="small" dense>
				<TableHead>
					<TableRow>
						<TableCell>{intl.formatMessage({ id: "TYPE" })}</TableCell>

						{hasPluginPermission(session, "notificationsJson") && (
							<TableCell>{intl.formatMessage({ id: "NOTIFICATION_TYPE_JSON" })}</TableCell>
						)}

						{hasPluginPermission(session, "notificationsEmail") && (
							<TableCell>{intl.formatMessage({ id: "EMAIL" })}</TableCell>
						)}

						{hasPluginPermission(session, "messages") && (
							<TableCell>{intl.formatMessage({ id: "NOTIFICATION_INBOX" })}</TableCell>
						)}
					</TableRow>
				</TableHead>

				<TableBody>
					{items.map((row, index) => {
						return (
							<TableRow key={index}>
								<TableCell>
									<span style={{ fontSize: 12, fontWeight: 500 }}>
										{row.name}
									</span>
								</TableCell>

								{hasPluginPermission(session, "notificationsJson") && (
									<TableCell>
										<Checkbox
											checked={row.json.notification || row.json.alwaysEnabled}
											disabled={row.json.disabled}
											onChange={event => updateNotification(event, row.id, NotificationTypes.JSON, row.json.notification)} />
									</TableCell>
								)}

								{hasPluginPermission(session, "notificationsEmail") && (
									<TableCell>
										<Checkbox
											checked={row.email.notification || row.email.alwaysEnabled}
											disabled={row.email.disabled}
											onChange={event => updateNotification(event, row.id, NotificationTypes.EMAIL, row.email.notification)} />
									</TableCell>
								)}

								{hasPluginPermission(session, "messages") && (
									<TableCell>
										<Checkbox
											checked={!!(row.inbox.notification || row.inbox.alwaysEnabled)}
											disabled={row.inbox.disabled}
											onChange={event => updateNotification(event, row.id, NotificationTypes.INBOX, row.inbox.notification)} />
									</TableCell>
								)}
							</TableRow>
						);
					})}
				</TableBody>
			</Table>

			{loading && (
				<div className="loading">
					<CircularProgress size={24} />
				</div>
			)}
		</div>
	);
};

export default NotificationSettings;
